/**
 * Scope interpretation of an AI-captured ad-hoc question - the structured "scope brief" that
 * drives effort, extracted from the transcript and editable in the wizard. Surfaced on the ad-hoc
 * question card + the estimate, and fed to the estimator + grounding so scope is costed by what
 * was actually discussed: the problem, the boundary (in/out of scope), the work method, the
 * complexity drivers, and the assumptions.
 *
 * The brief is intentionally generic (vendor-neutral) and SCALABLE: the editable dimensions are
 * described once by {@link SCOPE_BRIEF}, which the wizard rendering and the extraction prompt both
 * read, so adding a dimension is one descriptor entry + one {@link ScopeMeta} field. Storage stays
 * the `deal_adhoc_field.scope_meta` jsonb (no migration); {@link ScopeMeta.extra} is the
 * forward-compat bag for dimensions not yet first-class (e.g. delivery activities).
 */
import { z } from 'zod';
/** All scope statuses, in costed-first display order. */
export const SCOPE_STATUS_VALUES = ['requested', 'ongoing', 'mentioned'];
/**
 * Canonical, user-facing scope-status labels - the SINGLE source so the questionnaire's Scope control,
 * the Scope tab's status pill, and anywhere else that names a status stay in lockstep (they had drifted
 * to "Deferred"/"Context" vs "Ongoing discussion"/"Not included in estimate"). The Prioritization
 * pane's compact table deliberately uses its own SHORTER labels (with these as tooltips).
 */
export const SCOPE_STATUS_LABELS = {
    requested: 'Requested',
    ongoing: 'Ongoing discussion',
    mentioned: 'Not included in estimate',
};
/**
 * Whether a scope item is COSTED (feeds the estimate / resource plan / SOW line items).
 * Single source of the exclude-list contract: only 'mentioned' and 'ongoing' are excluded -
 * an ABSENT/unknown status stays costed ("Absent => treated as 'requested'"), so legacy rows
 * and hand-added questions (scope_meta NULL) never silently drop out of the estimate.
 */
export function isCostedScopeStatus(status) {
    return status !== 'mentioned' && status !== 'ongoing';
}
/**
 * Whether an estimate line COUNTS - toward the estimate total and as a row in the SOW / export
 * line items. The one shared rule: the line is costed, OR a user pinned an hours override on it.
 * An override is an explicit human decision to cost the line, so it wins over a non-costed
 * ('mentioned' / 'ongoing') status. Without this the estimate total (which folds every override,
 * see `getEffectiveEstimateData`) and the SOW Service Hours table drifted apart.
 */
export function countsTowardEstimate(status, hasHoursOverride) {
    return hasHoursOverride || isCostedScopeStatus(status);
}
/** Slug shape for taxonomy-profile work-type extensions (see estimation-taxonomy.ts). */
const WORK_TYPE_SLUG_RE = /^[a-z][a-z0-9_]{2,32}$/;
/**
 * Ordered options for the work-type select. `definition` is the SINGLE SOURCE of each type's
 * meaning - the extraction prompt, the estimator prompt, and the UI Select helper all read it (via
 * {@link workTypeDefinitions}), so the definitions can never drift between layers. They are written
 * to be mutually exclusive (advisory != training != assessment != audit), which is what stops the
 * mislabeling that over/under-scoped past estimates.
 */
export const WORK_TYPE_OPTIONS = [
    {
        value: 'assessment',
        label: 'Assessment / advisory',
        definition: 'Review the current state and produce findings, recommendations, and best-practice / process / governance guidance - no build.',
    },
    {
        value: 'implementation',
        label: 'Implementation / configuration',
        definition: 'Build and stand up new functionality end-to-end, or set up and adjust settings, fields, roles, and workflows in an existing system - using standard (no-code) capabilities; delivery includes its own testing and go-live.',
    },
    {
        value: 'customization',
        label: 'Customization',
        definition: 'Custom development beyond standard configuration - scripts, custom code, custom objects/records, and bespoke logic; delivery includes its own testing and go-live.',
    },
    {
        value: 'integration',
        label: 'Integration',
        definition: 'Connect systems - APIs, sync, field mappings, and error handling.',
    },
    {
        value: 'troubleshooting',
        label: 'Troubleshooting / audit support',
        definition: 'Diagnose and fix specific existing issues, or support a formal audit (evidence, controls, reconciliations) - bounded to those issues, not a redesign.',
    },
    {
        value: 'training',
        label: 'Training',
        definition: 'Enablement, documentation, SOPs, and end-user/admin training.',
    },
    {
        value: 'migration',
        label: 'Data migration',
        definition: 'Map, cleanse, move, and reconcile data.',
    },
];
const WORK_TYPE_SET = new Set(WORK_TYPE_OPTIONS.map((o) => o.value));
/**
 * Retired canonical keys -> their surviving key (vocabulary consolidation), applied at every
 * scope-meta write path and label lookup so stored data authored under the old vocabulary keeps
 * resolving. 'testing'/'deployment' map to null: they are delivery phases of a build, not
 * standalone activities, so a legacy tag is simply dropped.
 */
export const LEGACY_WORK_TYPE_ALIASES = {
    configuration: 'implementation',
    advisory: 'assessment',
    audit: 'troubleshooting',
    testing: null,
    deployment: null,
};
/** Resolve a raw stored key through the legacy aliases: canonical key, or undefined to drop. */
function resolveWorkTypeKey(v) {
    if (typeof v !== 'string')
        return undefined;
    if (v in LEGACY_WORK_TYPE_ALIASES)
        return LEGACY_WORK_TYPE_ALIASES[v] ?? undefined;
    return WORK_TYPE_SET.has(v) || WORK_TYPE_SLUG_RE.test(v) ? v : undefined;
}
/** Coerce arbitrary input to a valid WorkType (legacy keys map to their merged successor), or undefined. */
export function asWorkType(v) {
    const resolved = resolveWorkTypeKey(v);
    return resolved && WORK_TYPE_SET.has(resolved) ? resolved : undefined;
}
/** Short human label for one or more WorkTypes (badge / select); legacy keys label as their successor. */
export function workTypeLabel(wt) {
    const arr = Array.isArray(wt) ? wt : [wt];
    return arr
        .map((t) => {
        const key = t in LEGACY_WORK_TYPE_ALIASES ? LEGACY_WORK_TYPE_ALIASES[t] : t;
        if (key == null)
            return null;
        return WORK_TYPE_OPTIONS.find((o) => o.value === key)?.label ?? key;
    })
        .filter((s) => s != null)
        .join(', ');
}
/**
 * The work-type definitions rendered as a prompt block ("value = definition" per line). The single
 * source fed to both the extraction prompt and the estimator prompt so they agree on what each
 * type means.
 */
export function workTypeDefinitions() {
    return WORK_TYPE_OPTIONS.map((o) => `${o.value} = ${o.definition}`).join('\n');
}
/**
 * Union NEW refs (kept first - "an update surfaces the NEW doc's reference") with previously
 * stored refs, deduped by (documentUuid + whitespace-normalized quote), capped. Pure. Used by the
 * discovered-questions apply path so a re-processed item ACCUMULATES its meeting provenance
 * instead of losing earlier meetings' refs to the full scope_meta replace.
 */
export function mergeSourceRefs(newRefs, oldRefs, cap = 8) {
    const seen = new Set();
    const out = [];
    for (const ref of [...(newRefs ?? []), ...(oldRefs ?? [])]) {
        if (!ref?.documentUuid || !ref.quote)
            continue;
        const key = `${ref.documentUuid} ${ref.quote.replace(/\s+/g, ' ').trim().toLowerCase()}`;
        if (seen.has(key))
            continue;
        seen.add(key);
        out.push(ref);
        if (out.length >= cap)
            break;
    }
    return out;
}
// ── Coercion helpers (resilient: malformed input becomes undefined, never throws) ──────────────
/** A short free-text dimension: trim, drop empties, cap length. */
const briefText = z.unknown().transform((v) => {
    if (typeof v !== 'string')
        return undefined;
    const t = v.trim();
    return t.length ? t.slice(0, 2000) : undefined;
});
/** A tag-list dimension: keep non-empty trimmed strings, cap count. */
const briefTagList = (max) => z.unknown().transform((v) => {
    if (!Array.isArray(v))
        return undefined;
    const out = v
        .map((s) => (typeof s === 'string' ? s.trim() : ''))
        .filter((s) => s.length > 0)
        .slice(0, max);
    return out.length ? out : undefined;
});
/** The merged-from pieces of a consolidated question; each is coerced like a mini scope brief. */
const subProblemList = z.unknown().transform((v) => {
    if (!Array.isArray(v))
        return undefined;
    const out = [];
    for (const raw of v) {
        if (!raw || typeof raw !== 'object' || Array.isArray(raw))
            continue;
        const o = raw;
        const label = typeof o.label === 'string' ? o.label.trim().slice(0, 2000) : '';
        if (!label)
            continue;
        const sp = { label };
        if (typeof o.problem === 'string' && o.problem.trim())
            sp.problem = o.problem.trim().slice(0, 2000);
        if (Array.isArray(o.inScope)) {
            const ins = o.inScope
                .map((s) => (typeof s === 'string' ? s.trim() : ''))
                .filter((s) => s.length > 0)
                .slice(0, 20);
            if (ins.length)
                sp.inScope = ins;
        }
        if (Array.isArray(o.workType)) {
            const wt = [...new Set(o.workType.map(resolveWorkTypeKey))].filter((x) => x != null);
            if (wt.length)
                sp.workType = wt;
        }
        if (typeof o.hours === 'number' && Number.isFinite(o.hours) && o.hours >= 0)
            sp.hours = o.hours;
        if (typeof o.sourceFieldKey === 'string' && o.sourceFieldKey.trim())
            sp.sourceFieldKey = o.sourceFieldKey.trim();
        out.push(sp);
        if (out.length >= 50)
            break;
    }
    return out.length ? out : undefined;
});
/**
 * The single validator/cleaner for a {@link ScopeMeta}. Reused by the API capture/scope-meta
 * routes, `updateScopeMeta`, and discovery normalization so every write path coerces identically:
 * trims text, caps + de-blanks lists, clamps workType to the enum, defaults scopeStatus to
 * 'requested', and strips unknown top-level keys (forward-compat dimensions go in `extra`). Never
 * throws - bad input degrades to undefined per field.
 */
export const scopeMetaSchema = z.object({
    scopeStatus: z
        .unknown()
        .transform((v) => v === 'mentioned' ? 'mentioned' : v === 'ongoing' ? 'ongoing' : 'requested'),
    workType: z.unknown().transform((v) => {
        if (!v)
            return undefined;
        const raw = Array.isArray(v) ? v : [v];
        // Canonical keys OR slug-shaped taxonomy extensions pass; junk degrades to undefined.
        // Legacy keys from the retired vocabulary normalize to their merged successor (deduped -
        // e.g. ['implementation','configuration'] collapses to ['implementation']); retired
        // phase tags ('testing'/'deployment') drop. (Profile membership is validated at
        // profile-holding edges, not here - this schema runs at context-free write paths.)
        const valid = [...new Set(raw.map(resolveWorkTypeKey))].filter((x) => x != null);
        return valid.length > 0 ? valid : undefined;
    }),
    complexityDrivers: briefTagList(12),
    problem: briefText,
    proposedSolution: briefText,
    outcome: briefText,
    inScope: briefTagList(20),
    outOfScope: briefTagList(20),
    assumptions: briefTagList(20),
    notes: briefText,
    customerPriority: z
        .unknown()
        .transform((v) => v === 'low' || v === 'medium' || v === 'high' ? v : undefined),
    recommendedPriority: z
        .unknown()
        .transform((v) => v === 'low' || v === 'medium' || v === 'high' ? v : undefined),
    extra: z
        .unknown()
        .transform((v) => v && typeof v === 'object' && !Array.isArray(v) ? v : undefined),
    statusReason: briefText,
    // Verbatim transcript provenance quote (verified upstream by the discovery pipeline). Declared
    // so every write path preserves it - the schema strips unknown top-level keys. Capped at the
    // display length; the containment verification happened before it was ever stored.
    sourceQuote: z.unknown().transform((v) => {
        if (typeof v !== 'string')
            return undefined;
        const t = v.trim();
        return t.length ? t.slice(0, 240) : undefined;
    }),
    // Structured per-document provenance refs (verified upstream, like sourceQuote). Declared so
    // every write path preserves them - the schema strips unknown top-level keys. Resilient: an
    // entry missing documentUuid or quote degrades to dropped, never throws.
    sourceRefs: z.unknown().transform((v) => {
        if (!Array.isArray(v))
            return undefined;
        const out = [];
        for (const raw of v) {
            if (!raw || typeof raw !== 'object' || Array.isArray(raw))
                continue;
            const o = raw;
            const documentUuid = typeof o.documentUuid === 'string' ? o.documentUuid.trim() : '';
            const quote = typeof o.quote === 'string' ? o.quote.trim().slice(0, 240) : '';
            if (!documentUuid || !quote)
                continue;
            const ref = { documentUuid, quote };
            if (typeof o.documentTitle === 'string' && o.documentTitle.trim()) {
                ref.documentTitle = o.documentTitle.trim().slice(0, 300);
            }
            out.push(ref);
            if (out.length >= 8)
                break;
        }
        return out.length ? out : undefined;
    }),
    // Optional project-scale indicator ('Small' | 'Medium' | 'Large'). Declared for the same reason as
    // businessProcess below: it is a first-class {@link ScopeMeta} field READ by the grounding query
    // (renderItemText's `Project Size:` line), and an undeclared key is silently stripped by every
    // write path. Inert for existing producers - nothing wrote it before the scope-file import
    // (docs/plans/27), and the corpus side takes a `.pick()` of this schema, so it is unaffected.
    size: briefText,
    // Optional delivery/implementation phase (e.g. "Phase 1"). Declared so every write path keeps it.
    phase: briefText,
    // Coarse grouping key (≈ golden "Business Process"). Persisted like a brief text field so every
    // write path keeps it - the schema strips unknown top-level keys, so this must be declared here.
    businessProcess: briefText,
    // Merged-from pieces of a consolidated question (must be declared - the schema strips unknown keys).
    subProblems: subProblemList,
});
/** Parse + clean arbitrary input into a {@link ScopeMeta}. Safe (never throws). */
export function parseScopeMeta(input) {
    return scopeMetaSchema.parse(input ?? {});
}
/**
 * Column targets an uploaded scope list can map onto (docs/plans/27). Declared ONCE here because
 * three layers need the same vocabulary: the parser (packages/spreadsheet) detects them, the API
 * maps them onto {@link ScopeMeta}, and the dialog renders one dropdown per target. Every entry is
 * either a {@link SCOPE_BRIEF} dimension or one of the two grouping keys (label, businessProcess)
 * plus `size` - so a mapped column always has a home on ScopeMeta.
 *
 * Hours are deliberately absent: the estimator prices these rows, so an hours column in the source
 * file has nowhere to land (and is reported as ignored instead).
 */
export const SCOPE_IMPORT_TARGETS = [
    'label',
    'businessProcess',
    'problem',
    'proposedSolution',
    'outcome',
    'inScope',
    'outOfScope',
    'complexityDrivers',
    'workType',
    'size',
    'phase',
    'assumptions',
    'notes',
];
/** Targets whose cell text is a LIST (split on import); the rest are prose or a single value. */
export const SCOPE_IMPORT_LIST_TARGETS = [
    'inScope',
    'outOfScope',
    'complexityDrivers',
    'assumptions',
];
/**
 * User-facing label per target - the dialog's dropdown labels. Kept beside the vocabulary so a new
 * target can never ship without a name. Where a label differs from the SCOPE_BRIEF wording it is
 * because the upload speaks spreadsheet ("Line item", "System / application"), not brief.
 */
export const SCOPE_IMPORT_TARGET_LABELS = {
    label: 'Line item',
    businessProcess: 'Business process',
    problem: 'Problem / gap',
    proposedSolution: 'Proposed solution',
    outcome: 'Business outcome',
    inScope: 'In scope',
    outOfScope: 'Out of scope',
    complexityDrivers: 'System / complexity drivers',
    workType: 'Work type',
    size: 'Project size',
    phase: 'Phase',
    assumptions: 'Assumptions',
    notes: 'Notes',
};
/**
 * The scope brief, declared once. Order = display order, grouped Why -> What -> How-big -> Caveats.
 * Adding a dimension = one entry here + one optional field on {@link ScopeMeta} (and, for storage
 * beyond the named fields, the {@link ScopeMeta.extra} bag). The wizard maps over this and the
 * extraction prompt enumerates it, so a new dimension propagates without bespoke per-layer code.
 */
export const SCOPE_BRIEF = [
    {
        key: 'problem',
        label: 'Problem / gap',
        control: 'text',
        enhanceable: true,
        placeholder: 'What problem or gap does this address?',
        help: 'Why this work exists.',
        extractionHint: 'the gap, pain, or risk being solved',
        // Customer-facing estimate/SOW column - a one-liner reads as shallow next to the hand-written
        // estimates it replaces, so ask for real depth. EVIDENCE-CONDITIONAL by wording: depth pressure
        // on a thin item makes the model pad with invented impact (a bare system-name mention became a
        // fabricated "requires integration" ask that then got priced), so the shape must release the
        // pressure when the evidence is thin, never override the no-invention rule.
        textShape: 'up to 2-4 full sentences with the concrete specifics the evidence supports (systems, volumes, current pain, business impact) - only as much as the evidence carries; a bare mention stays one short sentence, never padded',
    },
    {
        key: 'proposedSolution',
        label: 'Proposed solution',
        control: 'text',
        enhanceable: true,
        primary: true,
        placeholder: 'How do we propose to solve it?',
        help: 'The approach at a high level.',
        extractionHint: 'how we propose to solve it - the approach/solution at a high level',
        textShape: 'up to 2-4 full sentences describing the approach and its key activities with the specifics the evidence supports - only as much as the evidence carries; a bare mention stays one short sentence, never padded',
    },
    {
        key: 'outcome',
        label: 'Business outcome',
        control: 'text',
        enhanceable: true,
        placeholder: 'What is the desired outcome?',
        help: 'The result the client wants.',
        extractionHint: 'the desired business result',
    },
    {
        key: 'inScope',
        label: 'In scope',
        control: 'tags',
        placeholder: 'Add an in-scope item',
        help: 'What is included.',
        extractionHint: 'concrete deliverables or work outputs explicitly included - what gets built or configured, NOT delivery phases or methods (use workType for those)',
    },
    {
        key: 'outOfScope',
        label: 'Out of scope',
        control: 'tags',
        placeholder: 'Add an out-of-scope item',
        help: 'What is explicitly excluded.',
        extractionHint: 'work explicitly excluded, future phases not in this engagement, or systems mentioned for context only with no work requested',
    },
    {
        key: 'complexityDrivers',
        label: 'Complexity drivers',
        control: 'tags',
        suggestionSource: 'drivers',
        primary: true,
        placeholder: 'Add a driver',
        help: 'What makes it big or hard.',
        extractionHint: 'concrete effort drivers (entity/subsidiary counts, currencies, manual processes, integrations, data volume)',
    },
    {
        key: 'assumptions',
        label: 'Assumptions',
        control: 'tags',
        placeholder: 'Add an assumption',
        help: 'What must be true for the estimate to hold.',
        extractionHint: 'client responsibilities or preconditions the estimate depends on',
    },
    {
        // User-only catch-all (no extractionHint -> not transcript-extracted, not fed to the estimator):
        // guarantees a free-form field on every card, including non-textarea ad-hoc questions.
        key: 'notes',
        label: 'Notes',
        control: 'text',
        enhanceable: true,
        placeholder: 'Anything else worth capturing',
        help: 'Free-form - anything not covered above.',
    },
];
//# sourceMappingURL=scope.js.map